/**
 * Small pieces shared by the pour location tables and add / edit dialogs
 * (pour create wizard + pour edit page).
 */
import React from 'react';
import { Box, Typography } from '@mui/material';
import type { LocationHardwareType } from '../../utils/locationFunction';

/** The device header rows name each hardware type in full. */
export const HARDWARE_TYPE_NAMES: Record<LocationHardwareType, string> = {
  'ConXagent': 'ConXagent',
  'ConXsensors': 'ConXsensor',
  'ConXone': 'ConXone',
  'TMC': 'Temperature Match Tank',
  'Weather Station': 'Weather Station',
  'Temperature & Humidity': 'ConXenviro (Temp & Humidity)',
  '': 'Hardware',
};

/** The tables' time cells: the date over the time and zone, so a time
 *  such as "22/09/2026, 00:00 GMT+3" takes two short lines, not one long one. */
export const TABLE_TIME_CELL_SX = {
  whiteSpace: 'normal',
  minWidth: 92,
  maxWidth: 118,
  fontSize: '0.8rem',
  lineHeight: 1.35,
} as const;

/** The edit and delete icon columns. */
export const TABLE_ICON_CELL_SX = { width: 36, px: '4px !important' } as const;

/** The location name column: long names wrap rather than being cut off. */
export const TABLE_NAME_CELL_SX = { minWidth: 176, maxWidth: 260 } as const;

/** The tick-box columns (Temp Limit, Evaporation): headers wrap to fit. */
export const TABLE_TICK_HEAD_SX = { whiteSpace: 'normal !important', width: 76, lineHeight: 1.25 } as const;

/** The concrete start column's short header; the full name and what it
 *  does go in its tooltip. */
export const CONCRETE_START_SHORT_LABEL = 'Concrete start';

/** A table cell that does not apply to the row's hardware or Function. */
export const NOT_APPLICABLE = (
  <Typography component="span" variant="body2" color="text.disabled">
    -
  </Typography>
);

/** One numbered step of the add / edit dialog. */
export const DialogStep: React.FC<{ n: number; title: string; children: React.ReactNode }> = ({
  n,
  title,
  children,
}) => (
  <Box sx={{ mb: 2 }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
      <Box
        sx={{
          width: 20,
          height: 20,
          borderRadius: '50%',
          bgcolor: '#1a1a2e',
          color: '#fff',
          fontSize: '0.7rem',
          fontWeight: 700,
          display: 'grid',
          placeItems: 'center',
        }}
      >
        {n}
      </Box>
      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
        {title}
      </Typography>
    </Box>
    {children}
  </Box>
);
